/**
 * A record of what the current page has fetched — keys only, never values.
 *
 * Sent with chat requests as `pageDataManifest`, so a question asked while
 * looking at a research page can be answered from the backend's own cache —
 * which this page's requests populated on their way through the proxy —
 * instead of re-fetching from the provider (ROADMAP 2026-08-17).
 *
 * The security shape is the point: the manifest carries `(symbol, action)`
 * pairs and nothing else. The backend never uses a value from the client; it
 * uses the pair to look up its own cache. A tampered manifest can at worst
 * cause a cache lookup that misses. That is why this module deliberately has
 * no way to attach data to an entry.
 *
 * A hit is also more *consistent*, not just cheaper: the number in the chat
 * reply is the same number rendered on the reader's screen, because both came
 * from the same cached payload.
 */

interface ManifestEntry {
  symbol: string;
  action: string;
  at: number;
}

/** More than any one page fetches; old entries evict first. */
const MAX_RECORDED = 40;

/** Entries older than this are not worth hinting — the backend cache's own
 *  TTLs are shorter for anything price-sensitive, so a stale hint just costs
 *  a miss. */
const MAX_AGE_MS = 10 * 60 * 1000;

/** The backend accepts at most this many (page_manifest.MAX_MANIFEST_ENTRIES);
 *  sending more would only be truncated there. */
const MAX_SENT = 12;

const entries = new Map<string, ManifestEntry>();

/** Called by the data layer on every successful proxy fetch. */
export function recordPageFetch(symbol: string, action: string): void {
  const sym = (symbol || '').toUpperCase().trim();
  if (!sym || !action) return;
  const key = `${sym}|${action}`;
  entries.delete(key); // re-insert at the end = most recent
  entries.set(key, { symbol: sym, action, at: Date.now() });
  while (entries.size > MAX_RECORDED) {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) break;
    entries.delete(oldest);
  }
}

/** The manifest to attach to a chat request: fresh entries, newest first. */
export function getPageDataManifest(): Array<{ symbol: string; action: string }> {
  const cutoff = Date.now() - MAX_AGE_MS;
  const fresh = Array.from(entries.values()).filter((e) => e.at >= cutoff);
  return fresh
    .sort((a, b) => b.at - a.at)
    .slice(0, MAX_SENT)
    .map(({ symbol, action }) => ({ symbol, action }));
}

/** Test hook. */
export function clearPageDataManifest(): void {
  entries.clear();
}
